import { useState, type FC, type ImgHTMLAttributes, type ReactEventHandler } from 'react'

import { mdiImageOffOutline } from '@mdi/js'
import DOMPurify from 'dompurify'
import { twJoin } from 'tailwind-merge'

import type { DomPurifyOptions } from './types'

const isLocalPath = (url: string): boolean =>
  url.startsWith('/') || url.startsWith('./') || url.startsWith('../') || url.startsWith('src/')

// Allow only safe protocols for absolute URLs
const getIsSafeURL = (url: string): boolean => {
  try {
    const parsed = new URL(url, window.location.origin) // Use base URL for relative paths
    return ['https:', 'http:'].includes(parsed.protocol)
  } catch {
    return false
  }
}

// Sanitize attributes with DOMPurify
const sanitizeAttribute = (value: string | undefined, options: DomPurifyOptions = {}): string => {
  if (!value || typeof value !== 'string') return value ?? ''
  return DOMPurify.sanitize(value, {
    ALLOWED_TAGS: [], // No tags, only attributes
    ALLOWED_ATTR: ['src', 'alt', ...(options.ALLOWED_ATTR || [])],
    ...options,
  })
}

export const Image: FC<
  Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'alt' | 'onError'> & {
    src: string
    alt?: string
    fallbackSrc?: string
    className?: string
    onError?: ReactEventHandler<HTMLImageElement>
    domPurifyOptions?: DomPurifyOptions
  }
> = ({
  src,
  alt,
  fallbackSrc = '/default.png',
  className,
  onError,
  domPurifyOptions = {},
  ...rest
}) => {
  const [failedSrc, setFailedSrc] = useState<string | null>(null)

  const sanitizedSrc = sanitizeAttribute(src, domPurifyOptions)
  // Allow local paths or safe absolute URLs; otherwise, use fallback
  const safeSrc =
    sanitizedSrc && (isLocalPath(sanitizedSrc) || getIsSafeURL(sanitizedSrc))
      ? sanitizedSrc
      : fallbackSrc
  const sanitizedAlt = sanitizeAttribute(alt, { ALLOWED_TAGS: [] })

  // Run the error handler once per src so a failing fallback can't retry forever
  const safeOnError: ReactEventHandler<HTMLImageElement> | undefined =
    typeof onError === 'function'
      ? e => {
          if (failedSrc === safeSrc) return
          setFailedSrc(safeSrc)
          onError(e)
        }
      : undefined

  return (
    <img src={safeSrc} alt={sanitizedAlt} className={className} onError={safeOnError} {...rest} />
  )
}

export const ImageWithIconFallback: FC<{
  src: string | null
  alt?: string
  fallbackIcon: React.ReactNode
  className?: string
  placeholder?: string
  domPurifyOptions?: DomPurifyOptions
}> = ({ src, alt, fallbackIcon, className, domPurifyOptions, placeholder }) => {
  const [hasError, setHasError] = useState(false)

  const sanitizedSrc = sanitizeAttribute(src || '', domPurifyOptions)
  const safeSrc = isLocalPath(sanitizedSrc) || getIsSafeURL(sanitizedSrc) ? sanitizedSrc : ''
  const sanitizedAlt = sanitizeAttribute(alt, { ALLOWED_TAGS: [] })

  return hasError || !safeSrc ? (
    <span className={twJoin(`flex items-center justify-center`, className)}>{fallbackIcon}</span>
  ) : (
    <Image
      src={safeSrc}
      alt={sanitizedAlt}
      className={className}
      placeholder={placeholder || mdiImageOffOutline}
      onError={() => setHasError(true)}
    />
  )
}
